
/**
 * Centralized API Client
 * 
 * Provides a single point of access for all API calls with consistent:
 * - Authentication handling
 * - Error handling
 * - Logging
 * - Request/response interceptors
 */
import { supabase } from '@/integrations/supabase/client';
import { isFeatureEnabled } from '@/utils/features/featureFlags';

// Define the base API URL - use environment variable or default to production
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://pumpd-webhook-app-5c7ade204a3d.herokuapp.com';

// Maximum number of retries for SMS endpoints
const MAX_SMS_RETRIES = 3;

// TypeScript interfaces to match axios types
interface RequestConfig {
  method?: string;
  url: string;
  headers?: Record<string, string>;
  data?: any;
  params?: Record<string, string>;
  timeout?: number;
}

interface ResponseType<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
}

/**
 * Checks if a URL is for an SMS-related endpoint
 */
function isSmsEndpoint(url: string): boolean {
  // Only recognize the new /api/sms/ pattern and the special case of /sms-billing/
  return url.includes('/api/sms/') || url.includes('/sms-billing/');
}

/**
 * Implements exponential backoff for retries
 */
function getBackoffDelay(attempt: number): number {
  // Base delay is 1 second, doubled for each attempt with a max of 10 seconds
  return Math.min(1000 * Math.pow(2, attempt), 10000);
}

/**
 * Extracts a user-friendly error message from a fetch error
 */
function getErrorMessage(error: any, url: string): string {
  // CORS-specific error detection
  if (error.message && (
    error.message.includes('Failed to fetch') || 
    error.message.includes('NetworkError') ||
    error.message.includes('Network request failed')
  )) {
    return isSmsEndpoint(url) ? 
      'Could not connect to SMS service. Please check your network connection and try again.' : 
      'Network connection error. Please check your internet connection.';
  }
  
  // Extract message from response data if available
  if (error.response && error.response.data) {
    return error.response.data.message || error.response.data.error || error.message;
  }
  
  return error.message || 'Unknown error occurred';
}

/**
 * Execute a fetch request with the given configuration
 */
async function fetchWithConfig<T = any>(config: RequestConfig, retryAttempt = 0): Promise<ResponseType<T>> {
  // Generate request ID for tracing if feature flag is enabled
  let requestId = '';
  if (isFeatureEnabled('enable_new_api_layer')) {
    requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }
  
  // Setup headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...config.headers || {}
  };
  
  // Add request ID if feature flag is enabled
  if (isFeatureEnabled('enable_new_api_layer')) {
    headers['X-Request-ID'] = requestId;
    
    // Add auth token if available - add better logging
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
        // Log token presence but not the actual token
        console.log(`[API Request] Auth token added to request [${requestId}]`);
      } else {
        console.warn(`[API Request] No auth token available for request [${requestId}]`);
      }
    } catch (err) {
      console.error('[API Error] Error getting auth session:', err);
    }
    
    // Log request
    console.log(`[API Request] ${config.method?.toUpperCase() || 'GET'} ${config.url} [${requestId}]${retryAttempt > 0 ? ` (Retry ${retryAttempt}/${MAX_SMS_RETRIES})` : ''}`);
  }
  
  // Build URL with query parameters
  let url = `${API_BASE_URL}${config.url}`;
  if (config.params) {
    const queryParams = new URLSearchParams();
    Object.entries(config.params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  
  // Setup request options
  const options: RequestInit = {
    method: config.method || 'GET',
    headers,
  };
  
  // Add request body for methods that support it
  if (config.data && config.method !== 'GET' && config.method !== 'HEAD') {
    options.body = JSON.stringify(config.data);
  }
  
  // Add timeout with proper cleanup mechanism
  const controller = new AbortController();
  options.signal = controller.signal;
  
  let timeoutId: number | undefined;
  
  try {
    // Set timeout if specified
    if (config.timeout) {
      timeoutId = window.setTimeout(() => {
        controller.abort();
      }, config.timeout);
    }
    
    // Execute fetch request
    const response = await fetch(url, options);
    
    // Parse response data
    const data = await response.json().catch(() => ({})) as T;
    
    // Create response object similar to axios
    const responseObj: ResponseType<T> = {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries()),
    };
    
    // Log response if feature flag is enabled
    if (isFeatureEnabled('enable_new_api_layer')) {
      console.log(`[API Response] ${response.status} ${config.method?.toUpperCase() || 'GET'} ${config.url} [${requestId}]`);
    }
    
    // Handle error responses
    if (!response.ok) {
      const error: any = new Error(response.statusText || 'Request failed');
      error.response = responseObj;
      error.config = config;
      
      // Handle specific status codes
      if (response.status === 401) {
        console.warn('Authentication error detected, signing out');
        await supabase.auth.signOut();
        window.location.href = '/auth?session_expired=true';
      }
      
      if (response.status === 429) {
        console.warn('Rate limit exceeded, implementing backoff strategy');
      }
      
      throw error;
    }
    
    return responseObj;
  } catch (error: any) {
    // Enhanced error logging for all requests
    const errorMessage = getErrorMessage(error, config.url);
    
    if (isFeatureEnabled('enable_new_api_layer')) {
      const status = error.response?.status || 'Network Error';
      console.error(`[API Error] ${status} ${config.method?.toUpperCase() || 'GET'} ${config.url} [${requestId}]:`, errorMessage);
      
      // Enhanced diagnostic information for SMS endpoints
      if (isSmsEndpoint(config.url)) {
        console.error(`[SMS API Diagnostics] [${requestId}]`, {
          endpoint: config.url,
          method: config.method || 'GET',
          status: error.response?.status,
          headers: headers,
          originHeader: headers['Origin'],
          userAgent: navigator.userAgent,
          online: navigator.onLine,
          error: error.message,
          retryAttempt
        });
      }
    }
    
    // Implement retry mechanism specifically for SMS endpoints with network errors
    if (isSmsEndpoint(config.url) && 
        retryAttempt < MAX_SMS_RETRIES && 
        (!error.response || error.message?.includes('fetch') || error.message?.includes('Network'))) {
      
      console.warn(`[SMS API] Retrying failed request (${retryAttempt + 1}/${MAX_SMS_RETRIES}): ${config.url}`);
      
      // Wait with exponential backoff before retrying
      const delay = getBackoffDelay(retryAttempt);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Retry the request
      return fetchWithConfig<T>(config, retryAttempt + 1);
    }
    
    throw error;
  } finally {
    // Always clean up the timeout to prevent memory leaks
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
  }
}

/**
 * API client that mimics axios interface
 */
export const apiClient = {
  get: <T = any>(url: string, config?: Omit<RequestConfig, 'url' | 'method'>) => 
    fetchWithConfig<T>({ ...config, url, method: 'GET' }),
  post: <T = any>(url: string, data?: any, config?: Omit<RequestConfig, 'url' | 'method' | 'data'>) => 
    fetchWithConfig<T>({ ...config, url, data, method: 'POST' }),
  put: <T = any>(url: string, data?: any, config?: Omit<RequestConfig, 'url' | 'method' | 'data'>) => 
    fetchWithConfig<T>({ ...config, url, data, method: 'PUT' }),
  delete: <T = any>(url: string, config?: Omit<RequestConfig, 'url' | 'method'>) => 
    fetchWithConfig<T>({ ...config, url, method: 'DELETE' }),
  patch: <T = any>(url: string, data?: any, config?: Omit<RequestConfig, 'url' | 'method' | 'data'>) => 
    fetchWithConfig<T>({ ...config, url, data, method: 'PATCH' }),
  request: <T = any>(config: RequestConfig) => fetchWithConfig<T>(config)
};

/**
 * Creates an API client (compatibility function)
 */
export const createApiClient = () => apiClient;

/**
 * Helper function to test API connectivity
 */
export async function testApiConnection(): Promise<any> {
  try {
    const response = await fetchWithConfig({
      url: '/api/debug/sms-billing',
      method: 'GET'
    });
    
    console.log('[API Test] Successfully connected to API:', response.data);
    return {
      success: true,
      ...response.data
    };
  } catch (error: any) {
    console.error('[API Test] Failed to connect to API:', error.message);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Helper function to make API requests with automatic error handling
 */
export async function apiRequest<T>(config: RequestConfig): Promise<T> {
  try {
    const response = await apiClient.request<T>(config);
    
    // Debug log for successful responses to help with troubleshooting
    console.log(`[API Success] ${config.method || 'GET'} ${config.url}`, {
      url: config.url,
      method: config.method || 'GET',
      status: response.status,
      dataPreview: typeof response.data === 'object' ? 
        JSON.stringify(response.data).substring(0, 200) + '...' : 
        'Non-object response'
    });
    
    return response.data;
  } catch (error: any) {
    // Enhanced error logging with specific handling for SMS endpoints
    const diagnosticInfo = {
      url: config.url,
      method: config.method || 'GET',
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      originalError: error.message
    };
    
    if (isSmsEndpoint(config.url)) {
      console.error(`[SMS API Error] ${config.method || 'GET'} ${config.url}`, {
        ...diagnosticInfo,
        isOnline: navigator.onLine,
        timeOfFailure: new Date().toISOString(),
      });
    } else {
      console.error(`[API Error] ${config.method || 'GET'} ${config.url}`, diagnosticInfo);
    }
    
    if (error.response) {
      // If we have a structured error response, use it
      const errorMessage = error.response.data?.message || error.response.data?.error || error.message;
      
      // Check for specific error types
      if (error.response.status === 401) {
        console.error('Auth error detected - will redirect to login');
        // Better handling for authorization errors
        setTimeout(() => {
          // Redirect to auth page after a short delay to ensure the error message is seen
          window.location.href = '/auth?session_expired=true';
        }, 2000);
        throw new Error('Authentication error: Your session has expired. Redirecting to login...');
      } else if (error.response.status === 403) {
        throw new Error('Permission denied: You do not have access to this resource.');
      } else if (error.response.status === 404) {
        throw new Error('Resource not found: The requested data could not be found.');
      } else if (error.response.status === 500) {
        throw new Error('Server error: There was a problem processing your request. Please try again later.');
      }
      
      throw new Error(errorMessage);
    }
    
    // Provide more informative error messages for network issues
    if (error.message === 'Network Error' || error.message.includes('Failed to fetch')) {
      // Add more helpful information for debugging network issues
      console.error('Network connectivity issue detected:', {
        url: config.url,
        fullUrl: API_BASE_URL + config.url,
        baseUrl: API_BASE_URL,
        navigator: navigator?.onLine ? 'Browser reports online' : 'Browser reports offline'
      });
      
      // For SMS billing specific endpoints, provide a more informative error
      if (config.url.includes('/sms-billing/')) {
        throw new Error('SMS billing service unavailable: Could not connect to the billing service. The SMS feature status may not display correctly until connectivity is restored. Please try again later.');
      } else if (isSmsEndpoint(config.url)) {
        throw new Error('SMS service connection failed: Unable to reach the SMS service. This may be due to network issues or the service may be temporarily unavailable.');
      } else {
        throw new Error('Network error: Please check your internet connection and try again.');
      }
    }
    
    throw error;
  }
}
